import React, { useEffect, useMemo, createContext, useContext } from "react";
import { useDialog } from "../hooks/useDialog";

// --- Context for sharing onHide between Modal and sub-components ---
interface ModalContextValue {
  onHide: () => void;
}

const ModalContext = createContext<ModalContextValue | undefined>(undefined);

/**
 * The hide callback of the enclosing `Modal`, or `undefined` when the
 * component is not rendered inside one. Lets content inside a modal dismiss
 * it without navigating — e.g. the widget's payment confirmation retry.
 */
export function useModalOnHide(): (() => void) | undefined {
  return useContext(ModalContext)?.onHide;
}

// --- Modal.Header ---
interface ModalHeaderProps {
  closeButton?: boolean;
  className?: string;
  children?: React.ReactNode;
}

function buildClassName(base: string, extra?: string): string {
  return extra ? `${base} ${extra}` : base;
}

const ModalHeader: React.FC<ModalHeaderProps> = ({ closeButton, className, children }) => {
  const onHide = useContext(ModalContext)?.onHide ?? (() => {});
  return (
    <div className={buildClassName("gc-modal-header", className)}>
      {children}
      {closeButton && (
        <button type="button" className="modal-close-btn" onClick={onHide} aria-label="Close">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>
      )}
    </div>
  );
};

// --- Simple sub-components (Title, Body, Footer) ---
interface ModalSectionProps {
  className?: string;
  children?: React.ReactNode;
}

function createModalSection(baseClass: string): React.FC<ModalSectionProps> {
  const Section: React.FC<ModalSectionProps> = ({ className, children }) => (
    <div className={buildClassName(baseClass, className)}>{children}</div>
  );
  Section.displayName = baseClass.replace(/-/g, " ");
  return Section;
}

const ModalTitle = createModalSection("gc-modal-title");
const ModalBody = createModalSection("gc-modal-body");
const ModalFooter = createModalSection("gc-modal-footer");

// --- Modal (parent) ---
interface ModalProps {
  show: boolean;
  onHide: () => void;
  centered?: boolean;
  dialogClassName?: string;
  ariaLabel?: string;
  children?: React.ReactNode;
}

const ModalComponent: React.FC<ModalProps> = ({
  show,
  onHide,
  centered = false,
  dialogClassName,
  ariaLabel,
  children,
}) => {
  const dialogRef = useDialog({ show, onClose: onHide, closeOnBackdropClick: true });

  // Scroll lock
  useEffect(() => {
    if (show) {
      const previousOverflow = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = previousOverflow;
      };
    }
  }, [show]);

  const contextValue = useMemo(() => ({ onHide }), [onHide]);

  const dialogClasses = ["custom-modal-dialog", centered ? "custom-modal-dialog--centered" : "", dialogClassName ?? ""]
    .filter(Boolean)
    .join(" ");

  // Do not render when hidden — matches react-bootstrap behaviour
  if (!show) return null;

  return (
    <ModalContext.Provider value={contextValue}>
      <dialog ref={dialogRef} className={dialogClasses} aria-modal="true" aria-label={ariaLabel}>
        <div className="gc-modal-content">{children}</div>
      </dialog>
    </ModalContext.Provider>
  );
};

// --- Compound component assembly ---
export const Modal = Object.assign(ModalComponent, {
  Header: ModalHeader,
  Title: ModalTitle,
  Body: ModalBody,
  Footer: ModalFooter,
});
